import axios from 'axios';
import { useSelectListsStore } from '@/stores/selectLists.js';
import { useAppStateStore } from '@/stores/appState.js';

// Track in-flight requests to prevent duplicate concurrent API calls
var pendingRequests = {};
// Local cache for synchronous access — Pinia state writes are reactive but
// not strictly synchronous to all consumers within the same tick, so we
// dedupe near-simultaneous fetches against a tiny in-memory cache.
var localCache = {};

export default {
    async getStoreDropdownData(storeName) {
        var url = '/SelectLists/' + storeName;
        const selectLists = useSelectListsStore();

        // Check local cache first (synchronously updated, always reliable)
        if (localCache[storeName] !== undefined) {
            return localCache[storeName];
        }

        // Check the Pinia store — single source of truth.
        const cached = selectLists.lists[storeName];
        if (Array.isArray(cached) ? cached.length : cached) {
            return cached;
        }

        // If a request is already in progress for this storeName, wait for it
        if (pendingRequests[storeName]) {
            return await pendingRequests[storeName];
        }

        // Start new request and track it
        pendingRequests[storeName] = axios
            .get(url)
            .then(response => {
                // Cache locally for immediate synchronous access by subsequent calls
                localCache[storeName] = response.data;
                // Write to Pinia so reactive consumers (p-select, p-advanced-select,
                // p-filter-select, StoreListManager mixin, etc.) update.
                selectLists.setList(storeName, response.data);
                delete pendingRequests[storeName];
                setTimeout(() => {
                    delete localCache[storeName];
                }, 1000);

                return response.data;
            })
            .catch(err => {
                delete pendingRequests[storeName];
                setTimeout(() => {
                    delete localCache[storeName];
                }, 1000);
                throw err;
            });

        return await pendingRequests[storeName];
    },

    // Skips both caches and refetches the list from the API, for when the data may have
    // changed since it was loaded and the caller can't wait on SignalR to catch up.
    async refreshStoreDropdownData(storeName) {
        delete localCache[storeName];
        useSelectListsStore().clearList(storeName);
        return await this.getStoreDropdownData(storeName);
    },

    async getSelectedLocationIdAsync() {
        let selectedLocationId = useAppStateStore().getSelectedLocationId
            ?? await axios.get('selectlists/singlelocationid').then((resp) => {
                        return resp.data;
                    });
        return  parseInt(selectedLocationId) || null;
    },

    // Formatters.js reads window.timeZone synchronously, so the store time zone
    // has to be known before the first screen renders. The router guard awaits
    // this once per page load; a failed request never blocks navigation (the
    // formatters fall back to the browser zone when the value is undefined).
    async ensureStoreTimeZoneLoadedAsync() {
        if (window.timeZone) {
            return window.timeZone;
        }

        try {
            const result = await this.getStoreDropdownData('storeTimeZone');
            if (result) {
                window.timeZone = result;
            }
        } catch (err) {
            // Non-fatal: leave window.timeZone undefined.
        }

        return window.timeZone;
    }
}
